import { useMemo } from "react";
import { useToken } from "src/business-hooks/useToken";
import { useTrackFeature } from "src/business-hooks/useTrackFeature";
import ModalWithNavigation from "src/components/general/ModalWithNavigation/ModalWithNavigation";
import { parseModalSectionParam } from "src/components/general/ModalWithNavigation/ModalWithNavigation.utils";
import { useGlobalLocalContext } from "src/storage/localStorage";
import SystemSettingsFooter, {
  type SystemSettingsTestAction,
} from "./components/SystemSettingsFooter/SystemSettingsFooter";
import SystemSettingsIntegrations from "./components/SystemSettingsIntegrations/SystemSettingsIntegrations";
import SystemSettingsSectionContent from "./components/SystemSettingsSectionContent/SystemSettingsSectionContent";
import SystemSettingsTokenManagement from "./components/SystemSettingsTokenManagement/SystemSettingsTokenManagement";
import SystemSettingsTokenManagementFooter from "./components/SystemSettingsTokenManagementFooter/SystemSettingsTokenManagementFooter";
import {
  CONTROL_PLANE_SECTION,
  INTEGRATIONS_SECTION,
  SECTIONS_WITH_TEST_EMAIL,
  TOKEN_MANAGEMENT_SECTION,
  WIDE_SECTIONS,
} from "./SystemSettings.constants";
import { useSystemSettings } from "./SystemSettings.hooks";
import {
  getVisibleSections,
  groupFieldsBySection,
  visibleSettingFields,
} from "./SystemSettings.utils";

// Hand-written sections that replace the schema-driven settings form.
const EXTRA_SECTIONS = {
  [TOKEN_MANAGEMENT_SECTION]: SystemSettingsTokenManagement,
  [INTEGRATIONS_SECTION]: SystemSettingsIntegrations,
} as const;

export default function SystemSettings() {
  useTrackFeature("system_settings");

  const isDebugMode = useGlobalLocalContext((state) => state.isDebugMode);
  // JWT claims resolve synchronously (unlike useCurrentUser's query), so the
  // admin-gated nav items are present from the first render and the section
  // list never shifts under the user (or an e2e click) mid-interaction.
  const { loggedInUser } = useToken();
  const {
    isOpen,
    value,
    setValue,
    schemaQuery,
    systemSettingsQuery,
    form,
    sendTestEmail,
    canSendTestEmail,
    testControlPlane,
    canTestControlPlane,
  } = useSystemSettings();

  const testActionFor = (section: string): SystemSettingsTestAction | undefined => {
    if (canSendTestEmail && SECTIONS_WITH_TEST_EMAIL.includes(section)) {
      return {
        label: "Send test email",
        onClick: () => sendTestEmail.mutate(),
        isLoading: sendTestEmail.isPending,
        trackingAction: "send_test_email",
      };
    }
    if (canTestControlPlane && section === CONTROL_PLANE_SECTION) {
      return {
        label: "Test connection",
        onClick: () => testControlPlane.mutate(),
        isLoading: testControlPlane.isPending,
        trackingAction: "test_control_plane",
      };
    }
    return undefined;
  };

  // Group fields by section
  const grouped = useMemo(
    () => groupFieldsBySection(schemaQuery.data?.properties),
    [schemaQuery.data?.properties]
  );

  // Get sorted section names with visibility filter, plus Token Management.
  // The Integrations section edits tenant settings, so it is tenant-admin only.
  const sectionNames = useMemo(
    () => [
      ...getVisibleSections(grouped, isDebugMode),
      ...(loggedInUser?.is_tenant_admin ? [INTEGRATIONS_SECTION] : []),
      TOKEN_MANAGEMENT_SECTION,
    ],
    [grouped, isDebugMode, loggedInUser?.is_tenant_admin]
  );

  const resetFields = useMemo(
    () =>
      getVisibleSections(grouped, isDebugMode).flatMap((id) =>
        visibleSettingFields(grouped[id] ?? [], isDebugMode)
      ),
    [grouped, isDebugMode]
  );

  return (
    <ModalWithNavigation
      isOpen={isOpen}
      onClose={() => setValue(null)}
      title="System settings"
      sections={sectionNames}
      isForm
      // The form is seeded from the stored values: editing before they load would
      // save only the touched keys, and the backend resets the rest.
      loading={
        !!loggedInUser?.is_tenant_admin &&
        !(schemaQuery.isSuccess && systemSettingsQuery.isSuccess)
      }
      onSubmit={() => void form.handleSubmit()}
      testId="system-settings-modal"
      initialSection={parseModalSectionParam(value).section}
      onSectionChange={(id) => {
        if (id) setValue(id, { replace: true });
      }}
      sx={(section: string) =>
        WIDE_SECTIONS.includes(section) ? { maxWidth: 1200 } : {}
      }
      slots={{
        Footer: (selectedSection) =>
          selectedSection === TOKEN_MANAGEMENT_SECTION ? (
            <SystemSettingsTokenManagementFooter />
          ) : (
            <SystemSettingsFooter
              testAction={testActionFor(selectedSection)}
              form={form}
              resetFields={
                selectedSection === INTEGRATIONS_SECTION ? undefined : resetFields
              }
            />
          ),
      }}
    >
      {(selectedSection) => {
        const ExtraSection =
          EXTRA_SECTIONS[selectedSection as keyof typeof EXTRA_SECTIONS];

        return ExtraSection ? (
          <ExtraSection form={form} />
        ) : (
          <SystemSettingsSectionContent
            fields={grouped[selectedSection] ?? []}
            form={form}
          />
        );
      }}
    </ModalWithNavigation>
  );
}
